import React, { Component } from 'react';
import _ from 'lodash';
import { deleteIncident, getAllIncidents, getUnresolvedIncidentsCount, updateIncident } from '../../api/incidents';
import { buildMonthRange, truncateText } from '../../helpers';
import { formatDate } from '../../helpers/dates';
import { withRouter } from '../../helpers/withRouter';
import IncidentModal from '../blocks/calendar/IncidentModal';
import MainNav from '../blocks/navigation/MainNav';
import Select from '../elements/Select';
import type { IncidentListItem, Project, Router, User } from '../../types';

const STATUS_OPTIONS = [
  { id: '', title: 'All statuses' },
  { id: 'unresolved', title: 'Unresolved' },
  { id: 'resolved', title: 'Resolved' },
];

type Props = {
  currentUser: User;
  projects: Array<Project>;
  users: Array<User>;
  router: Router;
  onUpdateUnresolvedCount?: (count: number) => void;
};

type State = {
  incidents: Array<IncidentListItem>;
  loading: boolean;
  error: string | null;
  projectFilter: string;
  statusFilter: string;
  monthFilter: string;
  showCreateModal: boolean;
  editIncident: IncidentListItem | null;
  resolvingIncidentId: number | null;
  deletingIncidentId: number | null;
};

class Incidents extends Component<Props, State> {
  state: State = {
    incidents: [],
    loading: false,
    error: null,
    projectFilter: '',
    statusFilter: '',
    monthFilter: '',
    showCreateModal: false,
    editIncident: null,
    resolvingIncidentId: null,
    deletingIncidentId: null,
  };

  monthRange = buildMonthRange();

  componentDidMount() {
    this.fetchIncidents();
  }

  fetchIncidents = async () => {
    const { projectFilter, statusFilter, monthFilter } = this.state;

    this.setState({ loading: true, error: null });

    try {
      const incidents = await getAllIncidents({
        project_id: projectFilter ? Number(projectFilter) : undefined,
        status: statusFilter || undefined,
        month: monthFilter || undefined,
      });
      this.setState({ incidents, loading: false });
    } catch {
      this.setState({ error: 'Failed to load incidents', loading: false });
    }
  };

  handleProjectFilterChange = (event: any) => {
    this.setState({ projectFilter: event.target.value }, this.fetchIncidents);
  };

  handleStatusFilterChange = (event: any) => {
    this.setState({ statusFilter: event.target.value }, this.fetchIncidents);
  };

  handleMonthFilterChange = (event: any) => {
    this.setState({ monthFilter: event.target.value }, this.fetchIncidents);
  };

  refreshUnresolvedCount = async () => {
    if (this.props.onUpdateUnresolvedCount) {
      const count = await getUnresolvedIncidentsCount();
      this.props.onUpdateUnresolvedCount(count);
    }
  };

  handleReportIncidentClick = () => {
    this.setState({ showCreateModal: true });
  };

  handleCreateModalClose = () => {
    this.setState({ showCreateModal: false });
  };

  handleCreateSubmit = () => {
    this.setState({ showCreateModal: false });
    this.fetchIncidents();
    this.refreshUnresolvedCount();
  };

  handleEditClick = (incident: IncidentListItem) => {
    this.setState({ editIncident: incident });
  };

  handleEditModalClose = () => {
    this.setState({ editIncident: null });
  };

  handleEditSubmit = () => {
    this.setState({ editIncident: null });
    this.fetchIncidents();
    this.refreshUnresolvedCount();
  };

  handleRowClick = (incident: IncidentListItem) => {
    const { navigate } = this.props.router;
    const reported = new Date(incident.reported_at);
    const month = reported.getMonth() + 1;
    const year = reported.getFullYear();
    navigate(`/projects/${incident.project_id}?tab=incidents&month=${month}&year=${year}`);
  };

  handleDeleteClick = async (incident: IncidentListItem) => {
    if (!window.confirm(`Delete incident "${incident.title}"?`)) return;

    this.setState({ deletingIncidentId: incident.id, error: null });

    try {
      await deleteIncident(incident.project_id, incident.id);
      this.setState({ deletingIncidentId: null });
      await this.fetchIncidents();
      await this.refreshUnresolvedCount();
    } catch {
      this.setState({
        error: 'Failed to delete incident',
        deletingIncidentId: null,
      });
    }
  };

  handleMarkResolved = async (incident: IncidentListItem) => {
    this.setState({ resolvingIncidentId: incident.id, error: null });

    try {
      await updateIncident(incident.project_id, incident.id, { status: 'resolved' });
      this.setState({ resolvingIncidentId: null });
      await this.fetchIncidents();
      await this.refreshUnresolvedCount();
    } catch {
      this.setState({
        error: 'Failed to mark incident as resolved',
        resolvingIncidentId: null,
      });
    }
  };

  render() {
    const {
      incidents, loading, error, projectFilter, statusFilter, monthFilter,
      showCreateModal, editIncident, resolvingIncidentId, deletingIncidentId,
    } = this.state;
    const { currentUser, projects, users } = this.props;
    const { navigate } = this.props.router;

    const projectOptions = [
      { id: '', title: 'All projects' },
      ...projects.map(p => ({ id: p.id, title: p.title })),
    ];

    return (
      <div className="container-view view-wrapper projects-view">
        <MainNav
          currentUser={currentUser}
          navigate={navigate}
        />
        <div className="container container-header mt-4">
          <div className="upper-header-wrapper">
            <div className="left-column">
              <h2>Incidents</h2>
              <hr className="float-left" />
            </div>
            <div className="right-column">
              <button
                type="button"
                className="btn btn-primary primary-color"
                onClick={this.handleReportIncidentClick}
              >
                Report Incident
              </button>
            </div>
          </div>
        </div>

        <div className="container mt-3">
          <div className="row mb-3">
            <div className="col-md-4">
              <Select
                name="project_filter"
                label="Project"
                id="project_filter"
                selectClass="form-control"
                value={projectFilter}
                items={projectOptions}
                onSelectChange={this.handleProjectFilterChange}
                includeChooseOption={false}
              />
            </div>
            <div className="col-md-4">
              <Select
                name="status_filter"
                label="Status"
                id="status_filter"
                selectClass="form-control"
                value={statusFilter}
                items={STATUS_OPTIONS}
                onSelectChange={this.handleStatusFilterChange}
                includeChooseOption={false}
              />
            </div>
            <div className="col-md-4">
              <Select
                name="month_filter"
                label="Month"
                id="month_filter"
                selectClass="form-control"
                value={monthFilter}
                items={this.monthRange}
                onSelectChange={this.handleMonthFilterChange}
                includeChooseOption={false}
              />
            </div>
          </div>

          {loading && <p className="text-center">Loading incidents...</p>}
          {error && <p className="text-danger text-center">{error}</p>}
          {!loading && incidents.length === 0 && (
            <p className="text-center text-muted">No incidents found.</p>
          )}

          {!loading && incidents.length > 0 && (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Title</th>
                  <th>Reported By</th>
                  <th>Reported At</th>
                  <th>Status</th>
                  <th>Reported Via</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map(incident => (
                  <tr
                    key={incident.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.handleRowClick(incident)}
                  >
                    <td>{incident.project_name}</td>
                    <td>{incident.title}</td>
                    <td>{incident.reported_by || '-'}</td>
                    <td>{formatDate(new Date(incident.reported_at), 'dd.MM.yyyy')}</td>
                    <td>
                      <span
                        className={`badge ${
                          incident.status === 'resolved'
                            ? 'badge-success'
                            : 'badge-warning'
                        }`}
                      >
                        {_.startCase(incident.status)}
                      </span>
                    </td>
                    <td>{_.startCase(incident.reported_via) || '-'}</td>
                    <td>{truncateText(incident.description)}</td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleEditClick(incident);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={deletingIncidentId === incident.id}
                          title="Delete incident"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleDeleteClick(incident);
                          }}
                        >
                          🗑
                        </button>
                        {incident.status === 'unresolved' && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={resolvingIncidentId === incident.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              this.handleMarkResolved(incident);
                            }}
                          >
                            {resolvingIncidentId === incident.id ? 'Saving…' : 'Resolve'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showCreateModal && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <IncidentModal
                  projects={projects}
                  users={users}
                  currentUser={currentUser}
                  onCloseModal={this.handleCreateModalClose}
                  onSubmit={this.handleCreateSubmit}
                />
              </div>
            </div>
          </div>
        )}

        {editIncident && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <IncidentModal
                  edit
                  incident={editIncident}
                  projects={projects}
                  users={users}
                  currentUser={currentUser}
                  onCloseModal={this.handleEditModalClose}
                  onSubmit={this.handleEditSubmit}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const ConnectedIncidents = withRouter(Incidents as any);

export default ConnectedIncidents;
